import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { mergeMap, skipWhile, take } from 'rxjs/operators';

import { isPermissionsSet } from './permissions-changed.utils';
import { SecurityService } from './security.service';

/**
 * Route guard that blocks navigation when the signed-in user has **no**
 * permissions for the current Software Security application.
 *
 * Companion to {@link SecurityGuard}, which only waits for permissions to
 * load and optionally filters on a feature-level `roles` expression.
 * `SecurityGuard` lets unmapped users through (empty permission list is
 * not a denial there); this guard treats an empty list as "not registered
 * for this app" and redirects to `data.fallbackUrl` (default `/access-denied`).
 *
 * Useful on the root route of an internal-only app where only a named
 * allowlist of UPS users is meant to reach the UI at all. Feature-level
 * gating inside the app should still use `SecurityGuard` + `data.roles`.
 */
@Injectable({ providedIn: 'root' })
export class AppAccessGuard {
  public static defaultFallbackUrl = '/access-denied';

  private readonly router = inject(Router);
  private readonly securityService = inject(SecurityService);

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> {
    const data = route.data || {};
    const fallbackUrl = (data['fallbackUrl']
      || AppAccessGuard.defaultFallbackUrl) as string;

    return this.securityService.permissionsChanged$.pipe(
      skipWhile((e) => !isPermissionsSet(e)),
      take(1),
      mergeMap(() => {
        const featureGroups = this.securityService.featureGroupsByName;
        const hasAny = Object.keys(featureGroups).length > 0;
        if (hasAny) return of(true);
        return of(this.router.parseUrl(fallbackUrl));
      }),
    );
  }
}
